import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc, trpcErrorCode } from "../../app/trpc.js";
import { Button, Chip, Input, Label, Notice, Sheet } from "../../ui/index.js";
import { addDays, atLocal, dayStart, fmtMoney, fmtTime, hhmmOf, newKey, useCabinet } from "../cabinet/index.js";
import { invalidateBookings } from "./invalidate.js";

type Mode = "booking" | "busy";

/**
 * Ручная запись «в два касания»: звонок или клиент с улицы: услуга, время, «Записать»;
 * имя и @ник/телефон необязательны (по ним склеивается карточка клиента). «Занято» это перерыв в графике.
 */
export function NewBookingSheet({ open, onClose, day, defaultMasterId }: { open: boolean; onClose: () => void; day: string; defaultMasterId: string | null }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cab = useCabinet();
  const tz = cab.timezone;
  const [mode, setMode] = useState<Mode>("booking");
  const [masterId, setMasterId] = useState<string | null>(defaultMasterId);
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [date, setDate] = useState(day);
  const [time, setTime] = useState("");
  const [until, setUntil] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [key, setKey] = useState(newKey);

  // Каждое открытие это новая запись: свой ключ идемпотентности, день из календаря.
  useEffect(() => {
    if (open) {
      setKey(newKey());
      setDate(day);
      setMasterId(defaultMasterId);
      setTime("");
      setUntil("");
      setName("");
      setContact("");
      setMode("booking");
      setServiceIds([]);
    }
  }, [open, day, defaultMasterId]);

  const services = useQuery(trpc.calendar.services.list.queryOptions({ salonId: cab.salonId }, { enabled: open }));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId }, { enabled: open }));
  const master = masters.data?.find((m) => m.id === masterId);
  const offered = services.data?.filter((s) => !master || master.serviceIds.includes(s.id)) ?? [];
  const slots = useQuery(
    trpc.calendar.slots.find.queryOptions(
      open && mode === "booking" && masterId && serviceIds.length > 0
        ? { salonId: cab.salonId, serviceIds, masterId, from: dayStart(date, tz), to: dayStart(addDays(date, 1), tz) }
        : skipToken,
    ),
  );

  // Другой мастер: услуги, которых он не делает, снимаем (иначе они скрыты, но уходят в запрос).
  const offeredKey = offered.map((s) => s.id).join(",");
  useEffect(() => {
    const allowed = new Set(offeredKey.split(","));
    setServiceIds((cur) => (cur.every((id) => allowed.has(id)) ? cur : cur.filter((id) => allowed.has(id))));
  }, [offeredKey]);

  const finish = () => {
    invalidateBookings();
    onClose();
  };
  const create = useMutation(trpc.calendar.bookings.createManual.mutationOptions({ onSuccess: finish }));
  const busy = useMutation(trpc.calendar.timeOff.set.mutationOptions({ onSuccess: finish }));
  const resetCreate = create.reset;
  const resetBusy = busy.reset;
  useEffect(() => {
    if (open) {
      resetCreate();
      resetBusy();
    }
  }, [open, resetCreate, resetBusy]);
  const code = trpcErrorCode(create.error);

  const toggleService = (id: string) => setServiceIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const total = offered.filter((s) => serviceIds.includes(s.id)).reduce((sum, s) => sum + s.price.amount, 0);
  const canBook = Boolean(masterId && serviceIds.length > 0 && time);
  const submit = () => {
    if (!masterId || !time) return;
    if (mode === "busy") {
      if (!until || until <= time) return;
      busy.mutate({ masterId, kind: "break", from: atLocal(date, time, tz), to: atLocal(date, until, tz) });
      return;
    }
    create.mutate({
      salonId: cab.salonId,
      masterId,
      serviceIds,
      start: atLocal(date, time, tz),
      idempotencyKey: key,
      ...(name.trim() || contact.trim() ? { newClient: { name: name.trim() || null, phoneOrHandle: contact.trim() || null } } : {}),
    });
  };

  return (
    <Sheet open={open} onClose={onClose} title={t("cal.new")}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Chip active={mode === "booking"} onClick={() => setMode("booking")}>
            {t("cal.newBooking")}
          </Chip>
          <Chip active={mode === "busy"} onClick={() => setMode("busy")}>
            {t("cal.busy")}
          </Chip>
        </div>

        {cab.isOwner && (masters.data?.length ?? 0) > 1 && (
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4">
            {masters.data?.map((m) => (
              <Chip key={m.id} active={masterId === m.id} onClick={() => setMasterId(m.id)}>
                {m.name}
              </Chip>
            ))}
          </div>
        )}

        {mode === "booking" && (
          <div>
            <Label>{t("cal.services")}</Label>
            <div className="flex flex-wrap gap-2">
              {offered.map((s) => (
                <Chip key={s.id} active={serviceIds.includes(s.id)} onClick={() => toggleService(s.id)}>
                  {s.name} · {s.durationMin}′
                </Chip>
              ))}
            </div>
            {services.data?.length === 0 && <p className="text-sm text-ink-3">{t("cal.noServices")}</p>}
          </div>
        )}

        <div>
          <Label htmlFor="nb-date">{t("cal.date")}</Label>
          <Input id="nb-date" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </div>

        {mode === "booking" && slots.data && (
          <div className="flex flex-wrap gap-2">
            {slots.data.slice(0, 24).map((s) => {
              const v = hhmmOf(s.start, tz);
              return (
                <Chip key={s.start.toISOString()} active={v === time} onClick={() => setTime(v)}>
                  {fmtTime(s.start, tz, lang)}
                </Chip>
              );
            })}
            {slots.data.length === 0 && <p className="text-sm text-ink-3">{t("cal.noFreeSlots")}</p>}
          </div>
        )}

        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="nb-time">{mode === "busy" ? t("cal.from") : t("cal.time")}</Label>
            <Input id="nb-time" type="time" required step={300} value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          {mode === "busy" && (
            <div className="flex-1">
              <Label htmlFor="nb-until">{t("cal.until")}</Label>
              <Input id="nb-until" type="time" required step={300} value={until} onChange={(e) => setUntil(e.target.value)} />
            </div>
          )}
        </div>

        {mode === "booking" && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="nb-name">{t("cal.clientName")}</Label>
              <Input id="nb-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
            </div>
            <div>
              <Label htmlFor="nb-contact">{t("cal.clientContact")}</Label>
              <Input id="nb-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="@marko · 064 123 4567" autoComplete="off" />
              <p className="mt-1 text-sm text-ink-3">{t("cal.clientContactHint")}</p>
            </div>
          </div>
        )}

        {create.isError && (
          <Notice kind="error">{code === "slot_taken" ? t("cal.slotTaken") : code === "validation" ? t("cal.badContact") : t("common.error")}</Notice>
        )}
        {busy.isError && <Notice kind="error">{t("common.error")}</Notice>}

        {mode === "booking" ? (
          <Button className="w-full" disabled={!canBook || create.isPending} onClick={submit}>
            {t("cal.book")}
            {total > 0 && ` · ${fmtMoney(total, lang)}`}
          </Button>
        ) : (
          <Button className="w-full" disabled={!masterId || !time || !until || until <= time || busy.isPending} onClick={submit}>
            {t("cal.markBusy")}
          </Button>
        )}
      </div>
    </Sheet>
  );
}
